import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Globe, Layers, Lock } from 'lucide-react';
import { logEvent } from './analytics';
import { imgProps } from './img';
import { categoryBySlug } from './categories';

// One project (or collection) card. Used on the home page, /projects and project pages.
// Black-and-white like the rest of the site: platform badge on the screenshot, hover overlay,
// summary, a few tools, and a footer with the address and a details link.
// priority: the first row on a page loads right away (it is usually the largest paint).
// Tools shown on a card: as many as fit one line (about 30 characters), so every card's tag row is a single row.
function cardTools(tech = []) {
  const out = [];
  let used = 0;
  for (const t of tech) {
    if (used + t.length > 30 || out.length === 3) break;
    out.push(t);
    used += t.length + 4;
  }
  return out.length ? out : tech.slice(0, 1);
}

const ProjectCard = ({ project: p, darkMode, compact = false, priority = false }) => {
  const to = p.isCollection ? p.link : `/project/${p.slug}`;
  const live = p.status === 'live' && p.link && !p.isCollection;
  const platform = categoryBySlug((p.categories || [])[0]);
  const muted = darkMode ? 'text-gray-400' : 'text-gray-600';
  const badge = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.6875rem] font-semibold backdrop-blur-sm';
  return (
    <article className={`group relative flex flex-col h-full rounded-2xl border overflow-hidden transition-all duration-300 hover:shadow-xl transform-gpu will-change-transform hover:-translate-y-1 ${
      darkMode ? 'bg-[#111] border-gray-800 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-400'}`}>
      <Link to={to} className="block relative aspect-[16/10] overflow-hidden" aria-label={p.title}
        onClick={() => logEvent('Project', 'Open', p.title)}>
        {p.image ? (
          <img {...imgProps(p.image, '(min-width: 1024px) 360px, (min-width: 640px) 45vw, 85vw')} alt={`${p.title} homepage`}
            loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} decoding="async" width="1280" height="800"
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-black">
            <span className="text-5xl font-black text-white/30 select-none">{p.title.charAt(0)}</span>
          </div>
        )}
        {/* hover overlay */}
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <span aria-hidden="true" className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 text-xs font-semibold text-white opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          {p.isCollection ? 'View collection' : 'View project'} <ArrowRight size={13} />
        </span>
        {/* badges */}
        <div className="absolute top-2.5 left-2.5 flex gap-1.5">
          {p.isCollection ? (
            <span className={`${badge} bg-gray-900/90 text-white`}><Layers size={11} /> Collection</span>
          ) : platform && (
            <span className={`${badge} bg-gray-900/90 text-white ring-1 ring-white/20 shadow-sm`}>{platform.label}</span>
          )}
          {p.status === 'offline' && <span className={`${badge} bg-gray-900/90 text-white ring-1 ring-white/20 shadow-sm`}>Archived</span>}
          {p.status === 'software' && <span className={`${badge} bg-gray-900/90 text-white ring-1 ring-white/20 shadow-sm`}><Lock size={10} /> Private</span>}
          {p.client === 'Personal project' && <span className={`${badge} bg-white/90 text-gray-900 ring-1 ring-black/10 shadow-sm`}>Personal project</span>}
        </div>
      </Link>

      <div className="flex flex-col flex-1 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className={`text-[0.9375rem] font-bold leading-snug ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <Link to={to} className="hover:underline underline-offset-2">{p.title}</Link>
          </h3>
          {live && (
            <a href={p.link} target="_blank" rel="noopener noreferrer" aria-label={`Open ${p.title} live site`}
              onClick={() => logEvent('Project', 'Click', p.title)}
              className={`flex-shrink-0 inline-flex items-center justify-center p-1.5 -m-1 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
              <ArrowUpRight size={16} />
            </a>
          )}
        </div>
        <p className={`mt-1.5 text-sm leading-relaxed line-clamp-2 ${muted}`}>{p.summary || p.description}</p>
        {(p.tech || []).length > 0 && (
          <ul className="mt-3 flex flex-nowrap gap-1.5 overflow-hidden" aria-label="Built with">
            {cardTools(p.tech).map((t) => (
              <li key={t} className={`flex-shrink-0 whitespace-nowrap px-2 py-0.5 rounded-md text-[0.6875rem] font-medium border ${
                darkMode ? 'bg-gray-900 text-gray-300 border-gray-800' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>{t}</li>
            ))}
          </ul>
        )}
        <div className="mt-auto pt-4">
          <div className={`flex items-center justify-between gap-3 pt-3 border-t text-xs ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
            <span className={`flex items-center gap-1.5 min-w-0 ${muted}`}>
              <Globe size={12} className="flex-shrink-0" aria-hidden="true" />
              <span className="truncate">{p.display_url}</span>
            </span>
            <Link to={to} className={`flex-shrink-0 inline-flex items-center gap-1 font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} hover:underline underline-offset-2`}>
              Details <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ProjectCard;
